import React, { useMemo } from 'react';

import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { ShareContextCopyPromptItem } from './ShareContextCopyPromptItem.tsx';
import { buildShareContextPrompt, DEFAULT_SHARE_CONTEXT_LEVEL } from './shareContextPrompt.ts';
import { ShareContextRovoItem } from './ShareContextRovoItem.tsx';
import { ShareContextTargetItem } from './ShareContextTargetItem.tsx';
import { SHARE_CONTEXT_TOOL_TARGETS } from './shareContextTools.tsx';

export type ShareContextMenuItemsProps = {
	contentId?: string;
	/** Opens the host's own Rovo chat. Omitting it drops the Rovo item. */
	onAskRovo?: (prompt: string) => void;
};

/**
 * The share-context menu's content: Rovo, one item per AI surface target, then Copy as prompt.
 * Each item reports its own hand-off. Rendered only while the menu is open, so the link bridges
 * are read and the prompt built only when someone is about to use them.
 *
 * The menu has no context-level picker, so the deep links and Copy as prompt share the default
 * level's CLI prompt. Rovo is not one of `SHARE_CONTEXT_TOOLS`: it takes no deep link and carries
 * the links prompt instead.
 */
export function ShareContextMenuItems({
	contentId,
	onAskRovo,
}: ShareContextMenuItemsProps): React.JSX.Element {
	const { cloudId } = useSessionData();
	const prompt = useMemo(
		() =>
			buildShareContextPrompt({ contentId, cloudId, contextLevel: DEFAULT_SHARE_CONTEXT_LEVEL }),
		[contentId, cloudId],
	);

	return (
		<>
			<DropdownItemGroup>
				<ShareContextRovoItem contentId={contentId} onAskRovo={onAskRovo} />
				{SHARE_CONTEXT_TOOL_TARGETS.map(({ tool, target }) => (
					<ShareContextTargetItem
						key={`${tool.id}-${target.id}`}
						tool={tool}
						target={target}
						prompt={prompt}
					/>
				))}
			</DropdownItemGroup>
			<DropdownItemGroup hasSeparator>
				<ShareContextCopyPromptItem prompt={prompt} />
			</DropdownItemGroup>
		</>
	);
}
